import React from 'react';
import { 
  Users, MapPin, FileText, Camera, FileImage, Clock, BarChart3, 
  Phone, Mail, MapPinIcon, Calendar, DollarSign, Target, AlertCircle,
  TrendingUp, CheckCircle, Plus, ArrowRight, Shield, Star
} from 'lucide-react';

interface EnhancedOverviewProps {
  enquete: any;
  onNavigate?: (section: string, subsection?: string) => void;
  onAddPerson?: () => void;
  onAddLocation?: () => void;
  onAddNote?: () => void;
  onAddPhoto?: () => void;
  onAddFile?: () => void;
}

const EnhancedOverview: React.FC<EnhancedOverviewProps> = ({
  enquete,
  onNavigate,
  onAddPerson,
  onAddLocation,
  onAddNote,
  onAddPhoto,
  onAddFile
}) => {
  const StatCard = ({ 
    title, 
    count, 
    icon: Icon, 
    onClick, 
    isEmpty = false, 
    emptyMessage = '', 
    section,
    subsection 
  }: any) => (
    <div 
      className={`enhanced-card p-6 cursor-pointer transition-all duration-300 ${
        isEmpty ? 'border-dashed border-osint-orange/30' : ''
      }`}
      onClick={() => onClick ? onClick() : onNavigate?.(section, subsection)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-osint-orange/20 rounded-xl flex items-center justify-center">
              <Icon className="w-6 h-6 text-osint-orange" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">{title}</h3>
              <p className="text-3xl font-bold text-osint-orange">{count}</p>
            </div>
          </div>
          {isEmpty && emptyMessage && (
            <p className="text-gray-400 text-sm mb-3">{emptyMessage}</p>
          )}
        </div>
        <div className="text-osint-orange">
          <ArrowRight className="w-5 h-5" />
        </div>
      </div>
      {isEmpty && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className="w-full mt-3 px-4 py-2 bg-osint-orange/20 hover:bg-osint-orange/30 rounded-lg text-osint-orange text-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Ajouter</span>
        </button>
      )}
    </div>
  );

  const clientInfo = typeof enquete.client === 'object' ? enquete.client : null;

  return (
    <div className="enhanced-overview space-y-8">
      {/* Informations client intégrées */}
      <div className="enhanced-card p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span>Informations Client</span>
          </h2>
          <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-lg text-sm font-medium">
            Confidentiel
          </span>
        </div>

        {clientInfo ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Nom du client</label>
                <p className="text-white font-semibold text-lg">{clientInfo.nom}</p>
              </div>
              {clientInfo.typeClient && (
                <div>
                  <label className="text-gray-400 text-sm">Type de client</label>
                  <p className="text-white">{clientInfo.typeClient}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {clientInfo.telephone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-osint-orange" />
                  <div>
                    <label className="text-gray-400 text-sm">Téléphone</label>
                    <p className="text-white">{clientInfo.telephone}</p>
                  </div>
                </div>
              )}
              {clientInfo.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-osint-orange" />
                  <div>
                    <label className="text-gray-400 text-sm">Email</label>
                    <p className="text-white">{clientInfo.email}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {clientInfo.adresse && (
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="w-4 h-4 text-osint-orange mt-1" />
                  <div>
                    <label className="text-gray-400 text-sm">Adresse</label>
                    <p className="text-white">{clientInfo.adresse}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">Informations client non disponibles</p>
          </div>
        )}
      </div>

      {/* Détails de l'enquête */}
      <div className="enhanced-card p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <div className="w-10 h-10 bg-osint-orange rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <span>Détails de l'enquête</span>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <label className="text-gray-400 text-sm">Description</label>
              <p className="text-white mt-1">{enquete.description}</p>
            </div>
            
            {enquete.objectifs && (
              <div>
                <label className="text-gray-400 text-sm">Objectifs</label>
                <p className="text-white mt-1">{enquete.objectifs}</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <Calendar className="w-4 h-4 text-osint-orange" />
                <div>
                  <label className="text-gray-400 text-sm">Date de création</label>
                  <p className="text-white">{new Date(enquete.date_creation || enquete.dateCreation).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <DollarSign className="w-4 h-4 text-osint-orange" />
                <div>
                  <label className="text-gray-400 text-sm">Budget</label>
                  <p className="text-white font-semibold">{enquete.budget}€</p>
                </div>
              </div>
            </div>

            {enquete.elementsFournis && (
              <div>
                <label className="text-gray-400 text-sm">Éléments fournis</label>
                <p className="text-white mt-1">{enquete.elementsFournis}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques cliquables */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <span>Éléments de l'enquête</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title="Personnes"
            count={enquete?.personnes?.length || 0}
            icon={Users}
            section="elements"
            subsection="personnes"
            isEmpty={!enquete?.personnes?.length}
            emptyMessage="Commencez par ajouter une personne d'intérêt"
            onClick={onAddPerson}
          />
          
          <StatCard
            title="Lieux"
            count={enquete?.lieux?.length || 0}
            icon={MapPin}
            section="elements"
            subsection="lieux"
            isEmpty={!enquete?.lieux?.length}
            emptyMessage="Ajoutez des lieux d'intérêt"
            onClick={onAddLocation}
          />
          
          <StatCard
            title="Notes"
            count={enquete?.notes?.length || 0}
            icon={FileText}
            section="elements"
            subsection="notes"
            isEmpty={!enquete?.notes?.length}
            emptyMessage="Documentez vos observations"
            onClick={onAddNote}
          />
          
          <StatCard
            title="Photos"
            count={enquete?.photos?.length || 0}
            icon={Camera}
            section="media"
            subsection="photos"
            isEmpty={!enquete?.photos?.length}
            emptyMessage="Ajoutez des preuves visuelles"
            onClick={onAddPhoto}
          />
          
          <StatCard
            title="Fichiers"
            count={enquete?.fichiers?.length || 0}
            icon={FileImage}
            section="media"
            subsection="fichiers"
            isEmpty={!enquete?.fichiers?.length}
            emptyMessage="Joignez des documents"
            onClick={onAddFile}
          />
          
          <StatCard
            title="Timeline"
            count={enquete?.timeline?.length || 0}
            icon={Clock}
            section="timeline"
            isEmpty={!enquete?.timeline?.length}
            emptyMessage="Chronologie des événements"
          />
        </div>
      </div>

      {/* Services d'expert améliorés */}
      <div className="enhanced-card p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span>Services d'Expert Disponibles</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              title: "Expert Cyber",
              description: "Investigations numériques, recherche OSINT avancée",
              price: "À partir de 150€",
              rating: 4.9,
              speciality: "OSINT • Dark Web • Forensic"
            },
            {
              title: "Expert Terrain", 
              description: "Surveillance physique, filatures, enquêtes de terrain",
              price: "À partir de 200€",
              rating: 4.8,
              speciality: "Surveillance • Filature • Investigation"
            },
            {
              title: "Expert Juridique",
              description: "Conseil juridique, constitution de dossier, expertise légale",
              price: "À partir de 180€", 
              rating: 4.9,
              speciality: "Droit • Procédure • Conseil"
            }
          ].map((expert, index) => (
            <div key={index} className="enhanced-card p-6 hover:border-purple-500/30 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold text-lg">{expert.title}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-yellow-400 text-sm font-medium">{expert.rating}</span>
                  </div>
                </div>
                <span className="text-osint-orange font-semibold text-sm">{expert.price}</span>
              </div>
              
              <p className="text-gray-300 text-sm mb-3">{expert.description}</p>
              <p className="text-purple-400 text-xs mb-4">{expert.speciality}</p>
              
              <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-medium transition-colors">
                Contacter l'expert
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Progression de l'enquête */}
      <div className="enhanced-card p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span>Progression de l'enquête</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Collecte d'informations</h3>
            <p className="text-gray-400 text-sm">Rassemblement des éléments initiaux</p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 bg-osint-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-10 h-10 text-osint-orange" />
            </div>
            <h3 className="text-white font-semibold mb-2">Investigation en cours</h3>
            <p className="text-gray-400 text-sm">Analyse et recherches approfondies</p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-gray-400 font-semibold mb-2">Rapport final</h3>
            <p className="text-gray-500 text-sm">Synthèse et conclusions</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedOverview;